import React, { useEffect, useState, useMemo } from "react"

import { CheckCircle, XCircle, Send, Clock, AlertCircle, AlertTriangle } from 'lucide-react';

import { apiGetMediaElement, apiGetMediaVerifactur } from "../../../../services/Admin/media"
import { apiLogVerifactu } from "../../../../services/Admin/logVerifactu"

import TableC from "../../../../components/Admin/TableC"
import LoadingProgress from "../../../../components/LoadingProgress"
import { getMimeTypeByFormat } from "../../../../components/Admin/Media/mediaUtils";
import Card from "../../../../components/Admin/Card";
import { Box, Button } from "@mui/material";
import MotivoRechazoModal from "./MotivoRechazoModal";
import moment from "moment";
import useAuth from "../../../../hooks/useAuth";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 20,
    p: 4,
    minWidth: 750,
    maxWidth: 750,
    borderRadius: 3,
    position: "relative"
};

export default function Management() {
    const { auth } = useAuth();
    let verifactuCertificadoFechaExpiracion = null;
    let diasParaExpirarVerifactu = null;
    let IconComponent = CheckCircle;
    let iconColor = '#4caf50'; // verde
    const unix = auth?.primaryUser?.verifactuCertificadoFechaExpiracion;
    if (unix) {
        verifactuCertificadoFechaExpiracion = moment.unix(unix).locale('es').format('D MMM, YYYY');
        diasParaExpirarVerifactu = moment.unix(unix).diff(moment(), 'days');
        if (diasParaExpirarVerifactu <= 7) {
            IconComponent = AlertTriangle;
            iconColor = '#f44336'; // rojo
        } else if (diasParaExpirarVerifactu <= 30) {
            IconComponent = AlertCircle;
            iconColor = '#ffcc00'; // amarillo
        }
    }
        const [openMotivoModal, setOpenMotivoModal] = useState(false);
        const [motivoRechazoContent, setMotivoRechazoContent] = useState("");

        const handleOpenMotivoRechazo = (motivo) => {
            setMotivoRechazoContent(motivo);
            setOpenMotivoModal(true);
        };
    const [transactions, setTransactions] = useState(null)
    // Estado para el filtro global de texto
    const [globalFilter, setGlobalFilter] = useState("");
    // Estado para el tipo de IVA
    const [loading, setLoading] = useState(true)

    // Filtrado por estado y texto global
    const filteredTransactions = useMemo(() => {
        let filtered = transactions || [];
        if (globalFilter) {
            const search = globalFilter.toLowerCase();
            filtered = filtered.filter(row =>
                Object.values(row).some(
                    val =>
                        typeof val === 'string' && val.toLowerCase().includes(search)
                )
            );
        }
        return filtered;
    }, [transactions, globalFilter]);

    const columns = useMemo(
        () => [
            { Header: "ID", accessor: "id" },
            { Header: "Nº Factura", accessor: "invoiceNumber" },
            {
                Header: "Estado AEAT",
                accessor: "verifactuEstadoAeat",
                Cell: ({ row }) => {
                    const estado = row.original.verifactuEstadoAeat;
                    // Mapeo de estados a texto, color e icono
                    const statusMap = {
                        pendiente: {
                            text: 'Pendiente',
                            color: '#FFA500',
                            Icon: Clock
                        },
                        enviado: {
                            text: 'Enviado',
                            color: '#1976d2',
                            Icon: Send
                        },
                        aceptado: {
                            text: 'Aceptado',
                            color: '#43a047',
                            Icon: CheckCircle
                        },
                        rechazado: {
                            text: 'Rechazado',
                            color: '#d32f2f',
                            Icon: XCircle
                        },
                        error: {
                            text: 'Error',
                            color: '#b71c1c',
                            Icon: XCircle
                        },
                        AceptadoConErrores: {
                            text: 'Aceptado',
                            color: '#FFA500',
                            Icon: AlertTriangle
                        },
                    };
                    const status = statusMap[estado] || { text: estado, color: '#757575', Icon: null };
                    const Icon = status.Icon;

                    return (
                        <>
                            {estado ? (
                                <span
                                    onClick={() => handleOpenMotivoRechazo(row.original.verifactuRespuestaAeat)}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '2px 12px',
                                        borderRadius: '12px',
                                        background: status.color + '22',
                                        color: status.color,
                                        fontWeight: 600,
                                        fontSize: '0.95em',
                                        letterSpacing: '0.5px',
                                        border: `1px solid ${status.color}`,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: 'box-shadow 0.2s',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                                    }}
                                    title="Ver motivo"
                                >
                                    {Icon && <Icon size={18} style={{ color: status.color, marginTop: "-8.5px" }} />}
                                    {status.text ? status.text.charAt(0).toUpperCase() + status.text.slice(1) : ''}
                                </span>
                            ) : null}
                        </>
                    );
                },
            },
            { Header: "Fecha Envío AEAT", accessor: "verifactuFechaEnvioAeat" },
            { Header: "Código Respuesta CSV AEAT", accessor: "verifactuAeatResponseCsvCode" },
            { 
                Header: "Huella (Actual)",
                accessor: "verifactuHuella",
                Cell: ({ row }) => {
                    const estado = row.original.verifactuEstadoAeat?.toLowerCase();
                    const huella = row.original.verifactuHuella;
                    const numeroFactura = row.original.invoiceNumber;
                    const isTachar = estado === 'error' || estado === 'rechazado' || !numeroFactura;
                    return isTachar ? <s>{huella}</s> : <>{huella}</>;
                }
            },
            { 
                Header: "Huella (Anterior)",
                accessor: "verifactuHuellaAnterior",
                Cell: ({ row }) => {
                    const estado = row.original.verifactuEstadoAeat?.toLowerCase();
                    const huella = row.original.verifactuHuellaAnterior;
                    const numeroFactura = row.original.invoiceNumber;
                    const isTachar = estado === 'error' || estado === 'rechazado' || !numeroFactura;
                    return isTachar ? <s>{huella}</s> : <>{huella}</>;
                }
            },
            {
                Header: "Descargas",
                accessor: "descargas",
                Cell: ({ row }) => {
                    const file = row.original.file;
                    const xml = row.original.verifactuXml;
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!file}
                                onClick={() => file && handleDownload(file, `${row.original.invoiceNumber || 'archivo'}.pdf`)}
                            >
                                PDF
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!xml}
                                onClick={() => xml && handleDownloadXml(xml, `${row.original.invoiceNumber || 'archivo'}.xml`)}
                            >
                                XML
                            </Button>
                        </Box>
                    );
                }
            },
        ],
        []
    )

    const getFacturas = async () => {
        setLoading(true)
        apiLogVerifactu().then(resp => {
            const temp = resp.data.map((doc, key) => ({
                id: doc.id,
                invoiceNumber: doc.invoiceNumber,
                verifactuCsv: doc.verifactuCsv,
                verifactuCsvPrev: doc.verifactuCsvPrev,
                verifactuEstadoAeat: doc.verifactuEstadoAeat,
                verifactuCodigoQr: doc.verifactuCodigoQr,
                verifactuRespuestaAeat: doc.verifactuRespuestaAeat,
                verifactuFechaEnvioAeat: doc.verifactuFechaEnvioAeat ? moment.unix(doc.verifactuFechaEnvioAeat).locale('es').format('D MMM, YYYY | HH:mm') : null,
                verifactuXml: doc.verifactuXml,
                verifactuHuella: doc.verifactuHuella,
                verifactuHuellaAnterior: doc.verifactuHuellaAnterior,
                verifactuAeatResponseCsvCode: doc.verifactuAeatResponseCsvCode,
                file: doc.file,
            }));
            setTransactions(temp)
            setLoading(false)
        }).catch(error => {
            console.error(error)
            setLoading(false)
        })
    }

    const handleDownload = (file, filename) => {
        if (file) {
            // Si inicialmente inicia con https
            if (file.startsWith("https")) {
                window.open(file, "_blank")
                return
            }
            apiGetMediaElement(file).then(resp => {
                const base64Content = resp.data?.content;
                const format = 'pdf';
                if (base64Content) {
                    const filenameIn = filename || `archivo.${format || "pdf"}`;
                    const mimeType = getMimeTypeByFormat(format);
                    const link = document.createElement("a");
                    link.href = `data:${mimeType};base64,${base64Content}`;
                    link.download = filenameIn;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
        }
    };
    const handleDownloadXml = async (hrefXml, selectedFile) => {
        try {
            const resp = await apiGetMediaVerifactur(hrefXml);
            const base64Content = resp.data?.content;
            const format = resp.data?.format || selectedFile?.format || "xml";

            // Cogemos el fichero que hay al final del path de hrefXml si no viene otro nombre (formato: ID/xml/filename.xml)
            const parts = hrefXml.split("/");
            const lastPart = parts[parts.length - 1];

            if (base64Content) {
                const filename = lastPart || selectedFile?.verifactuXml || `archivo.${format}`;
                const mimeType = getMimeTypeByFormat(format);
                const link = document.createElement("a");
                link.href = `data:${mimeType};base64,${base64Content}`;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Error descargando XML:", error);
        }
    };

    useEffect(() => {
        getFacturas()
    }, [])

    return (
        <>
        <Card>
            <div className="cointainer cointainer-bt">
                <h1>Verifactu</h1>
                {verifactuCertificadoFechaExpiracion && (
                    <div
                        style={{
                            marginTop: 8,
                            marginBottom: 8,
                            color: iconColor,
                            fontWeight: 600,
                            fontSize: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            background: '#fff',
                            padding: '6px 12px',
                            borderRadius: 10,
                            userSelect: 'none',
                        }}
                    >
                        <IconComponent size={20} color={iconColor} style={{ marginRight: 8, flexShrink: 0, marginTop: "-10px" }} />
                        <span style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: 1 }}>
                            El certificado VERIFACTU expira el: {verifactuCertificadoFechaExpiracion}
                        </span>
                    </div>
                )}
            </div>

            <LoadingProgress loading={loading} />
            {!loading && (
                <>
                    <TableC
                        columns={columns}
                        data={filteredTransactions}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                    />
                    <MotivoRechazoModal
                        open={openMotivoModal}
                        setOpen={setOpenMotivoModal}
                        motivo={motivoRechazoContent}
                    />
                </>
            )}

        </Card>
        </>
    )
}
